import CommandHoverHint from './CommandHoverHint.jsx'
import { CommandContext } from './CommandContext.js'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCommandAccountSync } from './useCommandAccountSync.js'
import {
  COMMAND_BY_ID,
  COMMAND_DEFINITIONS,
  COMMAND_SCOPES,
  COMMAND_SOURCES,
  DEFAULT_BINDINGS,
  MACRO_POLICIES,
  buildEffectiveBindings,
  canInvokeCommand,
  createSequenceMatcher,
  detectBindingConflicts,
  formatSequence,
  isReservedBrowserSequence,
  normalizePlatform,
  normalizeSequence,
} from './commandModel.js'
import {
  COMMAND_CUSTOMIZATION_SCHEMA_VERSION,
  normalizeCommandBindingPreference,
} from '../lib/commandPreferenceStore.js'
import {
  dryRunCommandMacro,
  executeCommandMacro,
  normalizeCommandMacro,
} from '../lib/commandMacros.js'
import CommandPalette from '../components/commands/CommandPalette.jsx'
import ShortcutHelp from '../components/commands/ShortcutHelp.jsx'

const REGISTERED_PROMPT_IDS = new Set(['navigation.symbol'])
const COMMAND_REGISTRY = new Map(COMMAND_DEFINITIONS.map(command => [command.id, command]))

function activePlatform() {
  if (typeof navigator === 'undefined') return 'mac'
  const raw = navigator.userAgentData?.platform || navigator.platform || 'mac'
  try {
    return normalizePlatform(raw)
  } catch {
    return /win/i.test(raw) ? 'windows' : /linux/i.test(raw) ? 'linux' : 'mac'
  }
}

function activeScopesForPath(pathname) {
  const scopes = [COMMAND_SCOPES.GLOBAL]
  if (
    /^\/positions\/workspace(\/|$)/.test(pathname) ||
    /^\/positions\/(board|symbol)\/[^/]+$/.test(pathname) ||
    pathname === '/__demo/position-workspace-chart'
  ) {
    scopes.unshift(COMMAND_SCOPES.POSITION_WORKSPACE)
  }
  if (/^\/strategies(\/|$)/.test(pathname)) scopes.unshift(COMMAND_SCOPES.STRATEGY_WORKSPACE)
  return scopes
}

function userBindingId(commandId) {
  return `user.${commandId}`
}

function toModelBinding(preference) {
  return {
    id: preference.id,
    commandId: preference.commandId,
    sequence: preference.sequence,
    platform: preference.platform || 'all',
    enabled: preference.enabled !== false,
  }
}

function commandDescription(command) {
  if (command.id === 'command.palette.open') return 'Search every available Erevis command'
  if (command.id === 'command.help.open') return 'Review commands and current keyboard bindings'
  if (command.route) return `Navigate to ${command.title.replace(/^Go to /, '')}`
  if (command.id === 'position.move_stop.start') return 'Prepare a Move Stop workflow (OMS staging gate required)'
  if (command.id === 'position.asset.previous') return 'Immediately open the previous visible position or idea'
  if (command.id === 'position.asset.next') return 'Immediately open the next visible position or idea'
  if (command.id === 'position.left_column.focus') return 'Enter left-column keyboard navigation mode'
  if (command.id === 'position.right_column.focus') return 'Enter right-column keyboard navigation mode'
  if (command.id === 'position.watchlist.add_current_asset') return 'Verify and add the displayed asset to the selected custom watchlist'
  if (command.id === 'position.rail.previous') return 'Move the keyboard highlight to the previous visible rail item'
  if (command.id === 'position.rail.next') return 'Move the keyboard highlight to the next visible rail item'
  if (command.id === 'position.rail.open') return 'Open the position or idea currently highlighted in the left rail'
  if (command.id === 'position.actions.focus') return 'Open and focus the Quick Actions panel'
  if (command.id === 'position.move_stop.focus') return 'Highlight Move Stop without opening or submitting the workflow'
  return command.title
}

export function CommandProvider({ userId, children }) {
  return <AccountCommandProvider key={userId || 'signed-out'} userId={userId}>{children}</AccountCommandProvider>
}

function AccountCommandProvider({ userId, children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const platform = useMemo(activePlatform, [])
  const accountSync = useCommandAccountSync(userId)
  const { bindings: userBindings, macros, status: syncState, controller } = accountSync
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [notice, setNotice] = useState(null)
  const [recording, setRecording] = useState(null)
  const continuationTimerRef = useRef(null)
  const commandHandlersRef = useRef(new Map())

  useEffect(() => {
    if (accountSync.legacyAvailable) setNotice('Older key commands are preserved in this browser. Open Settings → Commands & Navigation to import them into your account after confirming they are yours.')
  }, [accountSync.legacyAvailable])

  const activeScopes = useMemo(
    () => activeScopesForPath(location.pathname),
    [location.pathname],
  )

  const disabledDefaultBindingIds = useMemo(() => {
    const validPreferences = userBindings
      .map(row => normalizeCommandBindingPreference(row, { commandRegistry: COMMAND_REGISTRY }))
      .filter(preference => {
        if (!preference) return false
        if (preference.enabled === false) return true
        return !isReservedBrowserSequence(preference.sequence, platform)
      })
    return DEFAULT_BINDINGS
      .filter(binding => validPreferences.some(preference => (
        preference.commandId === binding.commandId &&
        DEFAULT_BINDINGS.some(original => original.id === preference.replacesDefaultBindingId && original.commandId === binding.commandId)
      )))
      .map(binding => binding.id)
  }, [platform, userBindings])

  const effective = useMemo(() => buildEffectiveBindings({
    userBindings: userBindings.map(toModelBinding),
    disabledDefaultBindingIds,
    platform,
  }), [disabledDefaultBindingIds, platform, userBindings])

  const matcher = useMemo(() => createSequenceMatcher(effective.bindings, { platform }), [effective.bindings, platform])
  const activeScopeKey = activeScopes.join('|')
  useEffect(() => {
    matcher.reset()
  }, [matcher, activeScopeKey, paletteOpen, helpOpen])

  useEffect(() => {
    const release = event => matcher.releaseEvent(event)
    const reset = () => matcher.reset()
    const visibility = () => { if (document.hidden) reset() }
    window.addEventListener('keyup', release, true)
    window.addEventListener('blur', reset)
    document.addEventListener('visibilitychange', visibility)
    return () => {
      window.removeEventListener('keyup', release, true)
      window.removeEventListener('blur', reset)
      document.removeEventListener('visibilitychange', visibility)
      matcher.reset()
    }
  }, [matcher])

  const commandViews = useMemo(() => COMMAND_DEFINITIONS.map(command => {
    const bindings = effective.bindings.filter(binding => binding.commandId === command.id)
    const available = command.id !== 'position.move_stop.start' &&
      canInvokeCommand(command, { source: COMMAND_SOURCES.PALETTE, activeScopes }).allowed
    return {
      ...command,
      label: command.title,
      description: commandDescription(command),
      scopeLabel: command.scope.replaceAll('-', ' '),
      effectiveBinding: bindings.flatMap(binding => (
        binding.sequence.map(stroke => formatSequence([stroke], platform))
      )),
      available,
      unavailableReason: command.id === 'position.move_stop.start'
        ? 'Available only after the dedicated OMS staging authorization work'
        : available ? null : 'Not available in the current workspace',
      keywords: [command.category, command.id],
    }
  }), [activeScopes, effective.bindings, platform])

  const recordCommand = useCallback((command) => {
    setRecording(current => {
      if (!current || current.stoppedAtBarrier || command.macroPolicy === MACRO_POLICIES.FORBIDDEN) return current
      if (current.steps.at(-1)?.commandId === command.id) return current
      return {
        ...current,
        steps: [...current.steps, { type: 'command', commandId: command.id }],
        stoppedAtBarrier: command.macroPolicy === MACRO_POLICIES.INTERACTION_BARRIER,
      }
    })
  }, [])

  const registerCommandHandler = useCallback((commandId, handler) => {
    if (!COMMAND_BY_ID[commandId] || typeof handler !== 'function') return () => {}
    commandHandlersRef.current.set(commandId, handler)
    return () => {
      if (commandHandlersRef.current.get(commandId) === handler) {
        commandHandlersRef.current.delete(commandId)
      }
    }
  }, [])

  const invokeCommand = useCallback(async (commandOrId, source = COMMAND_SOURCES.UI) => {
    const command = typeof commandOrId === 'string' ? COMMAND_BY_ID[commandOrId] : commandOrId
    const policy = canInvokeCommand(command, { source, activeScopes })
    if (!policy.allowed) return { ok: false, reason: policy.reason }

    if (source !== COMMAND_SOURCES.MACRO) recordCommand(command)
    if (command.id === 'command.palette.open') {
      setHelpOpen(false)
      setPaletteOpen(true)
      return { ok: true }
    }
    if (command.id === 'command.help.open') {
      setPaletteOpen(false)
      setHelpOpen(true)
      return { ok: true }
    }
    if (command.id === 'position.move_stop.start') {
      setNotice('Move Stop remains locked to the OMS staging implementation lane.')
      return { ok: false, barrier: true, reason: 'oms_staging_required' }
    }
    if (command.route) {
      navigate(command.route)
      return { ok: true }
    }
    const handler = commandHandlersRef.current.get(command.id)
    if (handler) {
      const result = await handler({ command, source })
      return result && typeof result === 'object' ? result : { ok: true }
    }
    return { ok: false, reason: 'no_handler' }
  }, [activeScopes, navigate, recordCommand])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (paletteOpen || helpOpen) { matcher.reset(); return }
      // Existing app modals own their keyboard state. Global/workspace
      // commands fail closed until the modal is dismissed.
      if (document.querySelector('[aria-modal="true"]')) { matcher.reset(); return }
      const result = matcher.pushEvent(event, {
        source: COMMAND_SOURCES.KEYBOARD,
        activeScopes,
      })
      if (!result.preventDefault) return
      event.preventDefault()
      event.stopImmediatePropagation()

      if (continuationTimerRef.current) window.clearTimeout(continuationTimerRef.current)
      if (result.status === 'matched') {
        invokeCommand(result.command, COMMAND_SOURCES.KEYBOARD)
        return
      }
      if (result.status === 'pending') {
        continuationTimerRef.current = window.setTimeout(() => {
          matcher.expirePending()
        }, Math.max(0, result.expiresAt - Date.now()))
        return
      }
      matcher.reset()
      if (result.status === 'ambiguous') setNotice('That key sequence is ambiguous. Change one binding in Settings.')
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
      if (continuationTimerRef.current) window.clearTimeout(continuationTimerRef.current)
    }
  }, [activeScopes, effective.bindings, helpOpen, invokeCommand, matcher, paletteOpen, platform])

  const previewBinding = useCallback((commandId, sequence) => {
    let normalized
    try {
      normalized = normalizeSequence(sequence)
    } catch (error) {
      return { valid: false, reason: error.message, conflicts: [] }
    }
    const candidate = {
      id: userBindingId(commandId),
      commandId,
      sequence: normalized,
      platform: 'all',
      enabled: true,
      scope: COMMAND_BY_ID[commandId]?.scope,
    }
    if (isReservedBrowserSequence(normalized, platform)) {
      return {
        valid: false,
        reason: 'That shortcut is reserved by the browser.',
        conflicts: [],
        sequence: normalized,
      }
    }
    const withoutCurrent = effective.bindings.filter(binding => binding.commandId !== commandId)
    const conflicts = detectBindingConflicts([...withoutCurrent, candidate], { platform })
      .filter(conflict => conflict.bindingIds.includes(candidate.id))
    return {
      valid: conflicts.length === 0,
      reason: conflicts.length ? 'This sequence conflicts with another active command.' : null,
      conflicts,
      sequence: normalized,
    }
  }, [effective.bindings, platform])

  const setBinding = useCallback(async (commandId, sequence) => {
    const preview = previewBinding(commandId, sequence)
    if (!controller.get().canEdit) return { ...preview, valid: false, reason: 'Account settings are not ready. Resolve synchronization in Commands & Navigation.' }
    if (!preview.valid) return preview
    const preference = {
      id: userBindingId(commandId),
      commandId,
      sequence: preview.sequence,
      platform: 'all',
      replacesDefaultBindingId: DEFAULT_BINDINGS.find(binding => binding.commandId === commandId)?.id || null,
      schemaVersion: COMMAND_CUSTOMIZATION_SCHEMA_VERSION,
      enabled: true,
    }
    const persisted = await controller.edit(current => ({ ...current, bindings: [...current.bindings.filter(row => row.commandId !== commandId), preference] }))
    return { ...preview, persisted }
  }, [previewBinding, controller])

  const resetBinding = useCallback((commandId) => controller.edit(current => ({
    ...current, bindings: current.bindings.filter(row => row.commandId !== commandId),
  })), [controller])

  const clearBinding = useCallback(async (commandId) => {
    const existing = userBindings.find(row => row.commandId === commandId)
    const shipped = DEFAULT_BINDINGS.find(binding => binding.commandId === commandId)
    const effectiveBinding = effective.bindings.find(binding => binding.commandId === commandId)
    if (!existing && !shipped && !effectiveBinding) return true
    const preference = {
      id: userBindingId(commandId), commandId,
      sequence: existing?.sequence || effectiveBinding?.sequence || shipped?.sequence,
      platform: existing?.platform || effectiveBinding?.platform || shipped?.platform || 'all',
      replacesDefaultBindingId: shipped?.id || null,
      schemaVersion: COMMAND_CUSTOMIZATION_SCHEMA_VERSION, enabled: false,
    }
    return controller.edit(current => ({ ...current, bindings: [...current.bindings.filter(row => row.commandId !== commandId), preference] }))
  }, [controller, effective.bindings, userBindings])

  const resetAllBindings = useCallback(() => controller.edit(current => ({ ...current, bindings: [] })), [controller])

  const saveMacro = useCallback(async (draft) => {
    const normalized = normalizeCommandMacro(draft, {
      commandRegistry: COMMAND_REGISTRY, registeredPromptIds: REGISTERED_PROMPT_IDS,
    })
    if (!normalized) return { ok: false, reason: 'invalid_macro' }
    if (!controller.get().canEdit) return { ok: false, reason: 'account_not_ready' }
    const persisted = await controller.edit(current => ({ ...current, macros: [...current.macros.filter(item => item.id !== normalized.id), normalized] }))
    return { ok: true, macro: normalized, persisted }
  }, [controller])

  const deleteMacro = useCallback((macroId) => controller.edit(current => ({ ...current, macros: current.macros.filter(item => item.id !== macroId) })), [controller])

  const dryRunMacro = useCallback((macro) => dryRunCommandMacro({
    macro,
    commandRegistry: COMMAND_REGISTRY,
    registeredPromptIds: REGISTERED_PROMPT_IDS,
  }), [])

  const runMacro = useCallback((macro) => executeCommandMacro({
    macro,
    commandRegistry: COMMAND_REGISTRY,
    registeredPromptIds: REGISTERED_PROMPT_IDS,
    invokeCommand: ({ commandId }) => invokeCommand(commandId, COMMAND_SOURCES.MACRO),
    requestPrompt: async ({ metadata }) => window.prompt(metadata.label),
  }), [invokeCommand])

  const startRecording = useCallback((name) => {
    const safeName = String(name || '').trim().slice(0, 80) || 'Recorded macro'
    setRecording({
      id: `macro-${Date.now()}`,
      name: safeName,
      schemaVersion: COMMAND_CUSTOMIZATION_SCHEMA_VERSION,
      enabled: true,
      steps: [],
      stoppedAtBarrier: false,
    })
  }, [])

  const stopRecording = useCallback(() => {
    const result = recording
    setRecording(null)
    return result
  }, [recording])

  const value = useMemo(() => ({
    commands: commandViews,
    effectiveBindings: effective.bindings,
    invalidBindings: effective.invalid,
    bindingConflicts: effective.conflicts,
    userBindings,
    macros,
    syncState,
    accountSync,
    notice,
    setNotice,
    invokeCommand,
    previewBinding,
    setBinding,
    clearBinding,
    resetBinding,
    resetAllBindings,
    saveMacro,
    deleteMacro,
    dryRunMacro,
    runMacro,
    recording,
    startRecording,
    stopRecording,
    registerCommandHandler,
    openPalette: () => setPaletteOpen(true),
    openHelp: () => setHelpOpen(true),
  }), [
    commandViews, deleteMacro, dryRunMacro, effective.bindings, effective.conflicts,
    effective.invalid, invokeCommand, macros, notice, previewBinding, recording,
    clearBinding, registerCommandHandler, resetAllBindings, resetBinding, runMacro,
    saveMacro, setBinding, startRecording, stopRecording, syncState, userBindings, accountSync,
  ])

  return (
    <CommandContext.Provider value={value}>
      {children}
      <CommandHoverHint bindings={effective.bindings} platform={platform} />
      <CommandPalette
        open={paletteOpen}
        commands={commandViews}
        onClose={() => setPaletteOpen(false)}
        onSelect={(command) => {
          setPaletteOpen(false)
          invokeCommand(command, COMMAND_SOURCES.PALETTE)
        }}
      />
      <ShortcutHelp open={helpOpen} commands={commandViews} onClose={() => setHelpOpen(false)} />
      {notice && (
        <div role="status" className="fixed bottom-6 right-6 z-[1100] max-w-sm rounded-lg border border-border bg-canvas px-4 py-3 text-sm text-ink shadow-xl">
          <div>{notice}</div>
          <button type="button" onClick={() => setNotice(null)} className="mt-2 font-mono text-[10px] uppercase tracking-widest text-inkMuted hover:text-ink">
            Dismiss
          </button>
        </div>
      )}
    </CommandContext.Provider>
  )
}

export function useCommands() {
  const context = useContext(CommandContext)
  if (!context) throw new Error('useCommands must be used inside CommandProvider')
  return context
}

export { useOptionalCommands } from './CommandContext.js'
